/**
 * MyTemplatesTab - Tab pentru gestionarea template-urilor personale
 * 
 * Features:
 * - Tabel cu toate template-urile
 * - Creare template nou
 * - Editare template
 * - Toggle favorite
 * - Ștergere template
 * - Search
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { doctorTemplatesApi, DoctorTemplate } from '../../services/doctorTemplatesApi';
import toast from 'react-hot-toast';

const QUILL_MODULES = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    ['clean']
  ]
};

const MyTemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<DoctorTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<DoctorTemplate | null>(null);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  
  // Form state
  const [formName, setFormName] = useState('');
  const [formContentHtml, setFormContentHtml] = useState('');
  const [formContentJson, setFormContentJson] = useState<any>(null);
  const [formIsFavorite, setFormIsFavorite] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  
  const quillRef = useRef<ReactQuill>(null);
  
  // Load templates
  useEffect(() => {
    loadTemplates();
  }, []);
  
  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await doctorTemplatesApi.getTemplates();
      setTemplates(data || []);
    } catch (err: any) {
      console.error('Error loading templates:', err);
      setError(err.response?.data?.detail || 'Nu s-au putut încărca template-urile');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreate = () => {
    setDialogMode('create');
    setEditingTemplate(null);
    resetForm();
    setDialogOpen(true);
  };
  
  const handleEdit = (template: DoctorTemplate) => {
    setDialogMode('edit');
    setEditingTemplate(template);
    setFormName(template.name);
    setFormContentHtml(template.content_html);
    setFormContentJson(template.content_json);
    setFormIsFavorite(template.is_favorite);
    setDialogOpen(true);
  };
  
  const handleDelete = async (template: DoctorTemplate) => {
    if (!window.confirm(`Sigur vrei să ștergi template-ul "${template.name}"?\n\nAceastă acțiune este ireversibilă!`)) {
      return;
    }
    
    try {
      await doctorTemplatesApi.deleteTemplate(template.id);
      toast.success('Template șters cu succes!');
      loadTemplates();
    } catch (err: any) {
      console.error('Error deleting template:', err);
      toast.error(err.response?.data?.detail || 'Eroare la ștergerea template-ului');
    }
  };
  
  const handleToggleFavorite = async (template: DoctorTemplate) => {
    try {
      const updated = await doctorTemplatesApi.toggleFavorite(template.id);
      setTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
      toast.success(updated.is_favorite ? 'Adăugat la favorite' : 'Eliminat din favorite');
    } catch (err: any) {
      console.error('Error toggling favorite:', err);
      toast.error('Eroare la actualizarea favoritelor');
    }
  };
  
  const resetForm = () => {
    setFormName('');
    setFormContentHtml('');
    setFormContentJson(null);
    setFormIsFavorite(false);
  };
  
  const handleSubmit = async () => {
    if (!formName.trim()) {
      toast.error('Te rog introdu un nume pentru template');
      return;
    }
    
    if (!formContentHtml.trim() || formContentHtml === '<p><br></p>') {
      toast.error('Te rog introdu conținut pentru template');
      return;
    }
    
    setFormSubmitting(true);
    
    try {
      const payload = {
        name: formName.trim(),
        content_html: formContentHtml,
        content_json: formContentJson || { ops: [] },
        is_favorite: formIsFavorite
      };
      
      if (dialogMode === 'create') {
        await doctorTemplatesApi.createTemplate(payload);
        toast.success('Template creat cu succes!');
      } else if (editingTemplate) {
        await doctorTemplatesApi.updateTemplate(editingTemplate.id, payload);
        toast.success('Template actualizat cu succes!');
      }
      
      setDialogOpen(false);
      resetForm();
      loadTemplates();
    } catch (err: any) {
      console.error('Error saving template:', err);
      toast.error(err.response?.data?.detail || 'Eroare la salvarea template-ului');
    } finally {
      setFormSubmitting(false);
    }
  };
  
  const handleQuillChange = (content: string, _delta: any, _source: any, editor: any) => {
    setFormContentHtml(content);
    setFormContentJson(editor.getContents());
  };
  
  // Filter templates
  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('ro-RO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };
  
  // Compact styles
  const inputSx = { '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } };
  const tableSx = { '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' } };
  const dialogPaperSx = {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    '& .MuiInputLabel-root': { color: 'text.secondary', fontSize: '0.85rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    '& .MuiOutlinedInput-root': {
      fontSize: '0.875rem',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    },
  };

  return (
    <Box>
      <Paper sx={{ p: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={handleCreate} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>Template Nou</Button>
          <TextField size="small" placeholder="Caută..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ width: 180, ...inputSx }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }} />
          <Box sx={{ flexGrow: 1 }} />
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Total: {templates.length}</Typography>
        </Box>
      </Paper>
      
      {error && <Alert severity="error" sx={{ mb: 1, py: 0.25, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>{error}</Alert>}
      
      <Paper sx={tableSx}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.900', '& th': { color: 'common.white', fontSize: '0.72rem', fontWeight: 600, py: 0.5, px: 1 } }}>
                <TableCell width={30}></TableCell>
                <TableCell>Nume</TableCell>
                <TableCell width={80}>Utilizări</TableCell>
                <TableCell width={90}>Creat</TableCell>
                <TableCell width={80} align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 2 }}><CircularProgress size={20} /></TableCell></TableRow>
              ) : filteredTemplates.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 2 }}><Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{searchQuery ? `Nu s-au găsit pentru "${searchQuery}"` : 'Niciun template. Creează primul!'}</Typography></TableCell></TableRow>
              ) : (
                filteredTemplates.map((template) => (
                  <TableRow key={template.id} hover sx={{ '& td': { py: 0.5, px: 1 } }}>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleToggleFavorite(template)} sx={{ p: 0.25 }}>
                        {template.is_favorite ? <StarIcon sx={{ fontSize: 16 }} color="warning" /> : <StarBorderIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </TableCell>
                    <TableCell><Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{template.name}</Typography></TableCell>
                    <TableCell><Chip label={template.usage_count} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} /></TableCell>
                    <TableCell><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{formatDate(template.created_at)}</Typography></TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
                        <IconButton size="small" onClick={() => handleEdit(template)} sx={{ p: 0.25 }}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                        <IconButton size="small" onClick={() => handleDelete(template)} color="error" sx={{ p: 0.25 }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Create/Edit Dialog - Compact & Consistent */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>{dialogMode === 'create' ? 'Creează Template Nou' : 'Editează Template'}</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
            <TextField label="Nume Template" placeholder="ex: CT_TAP, MR_COLOANA" value={formName} onChange={(e) => setFormName(e.target.value)} fullWidth required helperText="Nume descriptiv" size="small" sx={inputSx} />
            <FormControlLabel control={<Checkbox checked={formIsFavorite} onChange={(e) => setFormIsFavorite(e.target.checked)} size="small" />} label={<Typography sx={{ fontSize: '0.78rem' }}>Adaugă la favorite</Typography>} />
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, mb: 0.5 }}>Conținut Template</Typography>
              <Box sx={{ height: 280, border: 1, borderColor: 'divider', borderRadius: 1, '& .ql-container': { height: 'calc(100% - 42px)', fontSize: '0.85rem' } }}>
                <ReactQuill ref={quillRef} theme="snow" value={formContentHtml} onChange={handleQuillChange} modules={QUILL_MODULES} placeholder="Scrie conținutul..." style={{ height: '100%' }} />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setDialogOpen(false)} disabled={formSubmitting} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Anulează</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={formSubmitting || !formName.trim()} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>{formSubmitting ? <CircularProgress size={14} /> : (dialogMode === 'create' ? 'Creează' : 'Salvează')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyTemplatesTab;

